import { Row, Col, Container } from "react-bootstrap";
import {
  LoadingSpinner,
  ModalMessage,
  OrderDetails,
} from "../../components";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { fetchProductsByIdsApi } from "../../api/productsApi";
import { fetchUserApi } from "../../api/usersApi";
import {
  deleteFromStorageCart,
  updateStorageCart,
  clearStorage
} from "../../utilities/storageManager";
import {
  clearCart,
  deleteFromCart,
  updateCartQuantity,
} from "../../redux/cartSlice";
import getTaxAmount from "../../utilities/getTaxAmount";
import { getStorageToken } from "../../utilities";
import getShippingAmount from "../../utilities/getShippingAmount";
import { createOrderApi } from "../../api/ordersApi";
import { useNavigate } from "react-router-dom";

export default function UserCart() {
  // displays all items in ther user's cart along with 
  // user's shipping/payment info
  // user can edit quantities of products in the cart

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cartItems, quantity } = useSelector((state) => state.cart);
  const { isAuthenticated, stayLoggedIn, id, firstName, lastName } = useSelector(
    (state) => state.user
  );

  const [products, setProducts] = useState([]);
  const [cartSummary, setCartSummary] = useState(null);
  const [userData, setUserData] = useState(null);
  const [checkout, setCheckout] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [updateProductAvailability, setUpdateProductAvailability] =
    useState(true);

  useEffect(() => {
    const abortController = new AbortController();
    const fetchProducts = async (productIds) => {
      // returns array with jsons
      setIsLoading(true);
      try {
        const res = await fetchProductsByIdsApi(
          productIds,
          abortController.signal
        );
        return res;
      } catch (error) {
        setErrorMsg("Could not load data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };
    if(updateProductAvailability){
      const productIds = Object.keys(cartItems);
      fetchProducts(productIds)
      .then((res) => {
        setProducts(res);
        setUpdateProductAvailability(false);
      })
      .catch((error) => {
        setErrorMsg("Could not load data. Please try again later.");
      });
    }
    return () => abortController.abort();
  }, [updateProductAvailability, cartItems]);


  useEffect(() => {
    const calculateSummaryAmounts = (province = false) => {
      let cartSubtotal = 0;
      for (const product of products) {
        cartSubtotal += product.price * cartItems[product._id].count;
      }
      let shippingHandling = province
        ? getShippingAmount(province, cartSubtotal)
        : "TBD";
      const totalBeforeTax = province ? cartSubtotal + shippingHandling : "TBD";
      const tax = province
        ? getTaxAmount(province, cartSubtotal)
        : { taxAmount: "TBD", taxType: "" };
      const grandTotal = province ? totalBeforeTax + tax.taxAmount : "TBD";
      return {
        quantity,
        cartSubtotal,
        shippingHandling,
        totalBeforeTax,
        tax,
        grandTotal,
      };
    };

    if (
      products &&
      products.length > 0 &&
      cartItems &&
      Object.keys(cartItems).length > 0
    ) {
      const summaryAmounts = calculateSummaryAmounts(
        userData?.deliveryAddress?.province
      );
      setCartSummary(summaryAmounts);
    }
  }, [products, cartItems, userData?.deliveryAddress?.province, quantity]);


  const updateQuantity = (productId, newQuantity) => {
    //update store and storage
    dispatch(updateCartQuantity({ productId, count: newQuantity }));
    updateStorageCart({ productId, count: newQuantity }, stayLoggedIn);
  };

  const deleteItem = (productId) => {
    //update store and storage and products state variable
    dispatch(deleteFromCart(productId));
    deleteFromStorageCart(productId, stayLoggedIn);
    setProducts(products.filter((product) => product._id !== productId));
  };


  const handleAddressSubmit = (values) => {
    const formattedUserData = {
      user: { _id: id, name: firstName + " " + lastName },
      deliveryAddress: {
        name: values.name,
        street: values.street,
        city: values.city,
        province: values.province,
        postalCode: values.postalCode,
        country: values.country,
      },
    };
    setUserData(formattedUserData);
  };

  const handleCheckoutClick = async () => {
    if (isAuthenticated) {
      const userData = await fetchUserData(id);
      const formattedUserData = {
        user: { _id: id, name: firstName + " " + lastName },
        deliveryAddress: {
          name: userData.firstName + " " + userData.lastName,
          street: userData.street,
          city: userData.city,
          province: userData.province,
          postalCode: userData.postalCode,
          country: userData.country,
        },
      };
      setUserData(formattedUserData);
      // setCheckout(true);
    } 
    setCheckout(true); 
  };

  const handlePayNow = async (data, paymentMethod) => {
    ////handle pymt....
    const transactionResult = {
      status: "Paid",
      createTime: Date.now(),
      totalOrderPrice: data.orderTotal.grandTotal,
    };
    try {
      const orderId = await saveOrder(data, paymentMethod, transactionResult);
      if (orderId) {
        setSuccessMsg("Thank you! Your order has been placed!");
        // clear cart in store and storage
        dispatch(clearCart());
        clearStorage(["cart"]);
        await new Promise((resolve) => setTimeout(resolve, 1500));
        navigate(`/user/my-order-details/${orderId}`, { replace: true });
      }
    } catch (error) {
      console.log("error");
    }
  };

  const saveOrder = async (data, paymentMethod, transactionResult) => {
    try {
      setIsLoading(true);
      data.cartItems.forEach((item) => {
        item.category = item.categories[0]?.name;
        item.count = cartItems[item._id].count;
        item.image = item?.images[0]?.url;
        delete item.images;
      });
      const orderObject = {
        user: userData.user,
        orderTotal: data.orderTotal,
        cartItems: data.cartItems,
        paymentMethod,
        transactionResult,
        isPaid: true,
        paidAt: Date.now(),
        deliveryAddress: data.deliveryAddress,
      };
      const token = getStorageToken(stayLoggedIn);
      const abortController = new AbortController();
      // returns id
      const res = await createOrderApi(
        token,
        orderObject,
        abortController.signal
      );
      return res;
    } catch (error) {
      if (error?.status === 409) {
        setErrorMsg(
          "One or more items in your cart do not have enough inventory. Please remove those items before continuing to checkout."
        );
        setUpdateProductAvailability(true);

        return false;
      } else {
        setErrorMsg("Could not create order. Please try again later.");
        return false;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserData = async (id) => {
    // returns array with jsons
    try {
      setIsLoading(true);
      const token = getStorageToken(stayLoggedIn);
      const abortController = new AbortController();
      const res = await fetchUserApi(
        id,
        token,
        abortController.signal
      );
      return res;
    } catch (error) {
      setErrorMsg("Could not load data. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container className="mt-5">
      {errorMsg && (
        <ModalMessage
          type="danger"
          message={errorMsg}
          setErrorMsg={setErrorMsg}
        />
      )}
      {successMsg && (
        <ModalMessage
          type="success"
          message={successMsg}
          setSuccessMsg={setSuccessMsg}
        />
      )}
      {/* <Row>
        <Col>
          <h1 className="mb-3">Shopping Cart</h1>
        </Col>
      </Row> */}
      <Row>
        <Col>
          <h1>Shopping Cart</h1>
          {isLoading && <LoadingSpinner />}
          {products &&
            products.length > 0 &&
            cartItems &&
            Object.keys(cartItems).length > 0 && (
              <OrderDetails
                cart={isAuthenticated && checkout ? false : true}
                data={{
                  cartItems: products,
                  user: userData,
                  deliveryAddress: userData?.deliveryAddress,
                  orderTotal: {
                    quantity,
                    ...cartSummary,
                  },
                }} 
                cartItemQuantities={cartItems}
                updateQuantity={updateQuantity}
                deleteItem={deleteItem}
                handleCheckoutClick={handleCheckoutClick}
                handleAddressSubmit={handleAddressSubmit}
                handlePayNow={handlePayNow}
                totalQuantity={quantity}
                checkout={checkout}
              />
           
            )}
          {!isAuthenticated && checkout && (
            <ModalMessage
              message="Please log in before proceeding to the checkout page."
              linkPath="/login"
              nextPageLink="/cart"
              buttonText="Login"
              setSuccessMsg={setCheckout}
            />
          )}
          {!isLoading && (!products || products.length < 1) && (
            <p>Your cart is empty.</p>
          )}
        </Col>
      </Row>
    </Container>
  );
}
